/**
 * The "More" menu of the buy mode on a phone and a tablet held upright (snippets/imageset/orderimages.php,
 * the maintainer's answer 78): in a bar narrower than 48rem "Select all" and "Clear" leave the line of the bar for
 * a small menu above the button with the ellipsis, so the buy mode is one row - the count, "Done",
 * "More" and "Choose format". In a wider bar the button is hidden and the two buttons stand in the
 * line as before (components/bar.less); the stylesheet decides, this class opens and closes it and
 * keeps the open menu inside the bar (keepInsideTheBar()).
 *
 * The menu is a disclosure, not an ARIA menu: the toggle says whether it is open
 * (`aria-expanded`) and names the part it opens (`aria-controls`); Tab walks through its two
 * buttons. It closes with Escape (the focus back on the toggle), with a click beside it, when the
 * focus leaves it, after one of its buttons was used (the focus back on the toggle, so the buyer
 * stays at the same place of the bar) and when the buy mode ends.
 */
export class BuyBarMenu {
    static TOGGLE_SELECTOR = '.eventgallery-buy-bar-more-toggle';
    static GROUP_SELECTOR = '.eventgallery-buy-bar-more';
    /** the custom property which moves the open menu sideways (components/bar.less) */
    static SHIFT_PROPERTY = '--eg-bar-menu-shift';

    group: HTMLElement;
    toggle: HTMLButtonElement;
    menu: HTMLElement | null;

    constructor(group: HTMLElement) {
        this.group = group;
        this.toggle = group.querySelector(BuyBarMenu.TOGGLE_SELECTOR) as HTMLButtonElement;
        const id = this.toggle.getAttribute('aria-controls');
        this.menu = id ? document.getElementById(id) : null;

        this.toggle.addEventListener('click', (e: Event) => {
            e.preventDefault();
            this.setOpen(!this.isOpen());
        });

        // a button of the menu was used: the menu closes after its own listener ran
        this.menu?.addEventListener('click', (e: Event) => {
            if ((e.target as Element)?.closest?.('button') && this.isOpen()) {
                this.close(true);
            }
        });

        group.addEventListener('keydown', (e: KeyboardEvent) => {
            if (e.key === 'Escape' && this.isOpen()) {
                e.preventDefault();
                e.stopPropagation();
                this.close(true);
            }
        });

        group.addEventListener('focusout', (e: FocusEvent) => {
            const next = e.relatedTarget as Node | null;
            if (this.isOpen() && next && !group.contains(next)) {
                this.close(false);
            }
        });

        document.addEventListener('click', (e: Event) => {
            if (this.isOpen() && !group.contains(e.target as Node)) {
                this.close(false);
            }
        });

        // a phone turned while the menu is open: the button stands somewhere else
        window.addEventListener('resize', () => {
            if (this.isOpen()) {
                this.keepInsideTheBar();
            }
        });
    }

    /**
     * Binds the menu of every buy bar inside the container.
     */
    static bind(container: ParentNode): BuyBarMenu[] {
        return Array.from(container.querySelectorAll<HTMLElement>(BuyBarMenu.GROUP_SELECTOR))
            .filter((group) => group.querySelector(BuyBarMenu.TOGGLE_SELECTOR))
            .map((group) => new BuyBarMenu(group));
    }

    isOpen(): boolean {
        return this.toggle.getAttribute('aria-expanded') === 'true';
    }

    setOpen(open: boolean) {
        this.toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        if (open) {
            this.keepInsideTheBar();
        } else {
            this.menu?.style.removeProperty(BuyBarMenu.SHIFT_PROPERTY);
        }
    }

    /**
     * Keeps the open menu inside the bar, within its padding. The menu ends where the button ends
     * (components/bar.less), which leaves room enough while the button stands at the end of the
     * line; on a phone of 320 px the count takes a row of its own, the button moves towards the
     * middle and the menu, 12rem wide, left the window at its start ("elect all"). The menu moves
     * back by what is missing - towards the end in a bar from left to right, towards the start in
     * one from right to left - through --eg-bar-menu-shift; nothing moves while it fits.
     */
    keepInsideTheBar() {
        const menu = this.menu;
        if (!menu) {
            return;
        }
        menu.style.removeProperty(BuyBarMenu.SHIFT_PROPERTY);
        const box = menu.getBoundingClientRect();
        if (!box.width) {
            return;
        }
        const bar = this.group.closest('.eg-bar') as HTMLElement | null;
        const frame = bar ? bar.getBoundingClientRect() : {left: 0, right: document.documentElement.clientWidth};
        const style = bar ? getComputedStyle(bar) : null;
        const left = frame.left + (style ? parseFloat(style.paddingLeft) || 0 : 0);
        const right = frame.right - (style ? parseFloat(style.paddingRight) || 0 : 0);
        let shift = 0;
        if (box.left < left) {
            shift = left - box.left;
        } else if (box.right > right) {
            shift = right - box.right;
        }
        if (Math.round(shift) !== 0) {
            menu.style.setProperty(BuyBarMenu.SHIFT_PROPERTY, `${Math.round(shift)}px`);
        }
    }

    /**
     * Closes the menu; the focus goes back to the toggle when it was inside the menu or asked for.
     */
    close(focusToggle: boolean) {
        const hadFocus = this.menu !== null && this.menu.contains(document.activeElement);
        this.setOpen(false);
        if (focusToggle || hadFocus) {
            this.toggle.focus({preventScroll: true});
        }
    }
}
